/**
 * TrustedBy component - Auto-scrolling company logos
 * Infinite horizontal scroll animation
 */

import {Container} from '../ui/Container';
import {Section} from '../ui/Section';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

interface Company {
  name: string;
  logo: string | React.ReactNode;
}

const companies: Company[] = [
  {name: 'Docker', logo: '🐳 docker'},
  {name: 'GitHub', logo: 'GitHub'},
  {name: 'NVIDIA', logo: 'NVIDIA'},
  {name: 'Ramp', logo: 'ramp ↗'},
  {name: 'Asana', logo: 'asana'},
  {name: 'DoorDash', logo: 'DOORDASH'},
  {name: 'Retool', logo: 'Retool'},
  {name: 'VMware', logo: 'vmware'},
  {name: 'Amazon', logo: 'amazon'},
  {name: 'Microsoft', logo: 'Microsoft'},
  {name: 'Google', logo: 'Google'},
  {name: 'Stripe', logo: 'Stripe'},
];

export function TrustedBy() {
  const {ref, isVisible} = useScrollAnimation();
  // Duplicate companies for seamless infinite scroll
  const duplicatedCompanies = [...companies, ...companies];

  return (
    <Section id="trusted-by" padding="sm" background="gray" ref={ref} className="py-8">
      <Container>
        <div className={`text-center mb-4 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl sm:text-3xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-1">
            Trusted by Teams Worldwide
          </h2>
          <p className="text-sm sm:text-base text-textSecondary dark:text-[#858585]">
            Over half a million engineers at leading companies
          </p>
        </div>

        {/* Infinite Scrolling Logos */}
        <div className="relative overflow-hidden py-2">
          {/* Gradient fade on left */}
          <div className="absolute left-0 top-0 bottom-0 w-24 sm:w-32 bg-gradient-to-r from-backgroundSecondary dark:from-[#252526] via-backgroundSecondary dark:via-[#252526] to-transparent z-10 pointer-events-none" />
          
          {/* Gradient fade on right */}
          <div className="absolute right-0 top-0 bottom-0 w-24 sm:w-32 bg-gradient-to-l from-backgroundSecondary dark:from-[#252526] via-backgroundSecondary dark:via-[#252526] to-transparent z-10 pointer-events-none" />

          {/* Scrolling container */}
          <div className="flex gap-6 md:gap-8 lg:gap-12 animate-scroll group">
            {duplicatedCompanies.map((company, index) => (
              <div
                key={`${company.name}-${index}`}
                className="flex-shrink-0 flex items-center justify-center"
              >
                <div className="text-base sm:text-lg font-semibold text-textPrimary dark:text-[#CCCCCC] opacity-70 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                  {typeof company.logo === 'string' ? company.logo : company.logo}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(-50% - 1rem));
          }
        }

        .animate-scroll {
          animation: scroll 40s linear infinite;
          will-change: transform;
        }

        .animate-scroll:hover {
          animation-play-state: paused;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-scroll {
            animation: none;
          }
        }
      `}</style>
    </Section>
  );
}

